/* eslint-disable security/detect-object-injection */
/* eslint-disable camelcase */
import { useState, useEffect, useRef } from 'react'
import { styled } from '@mui/material/styles'
import { Box, Grid } from '@mui/material'
import {
  Edit,
  useRecordContext,
  useDataProvider,
  useNotify,
  RaRecord,
  useRefresh,
} from 'react-admin'
import ContactPanel from './ContactPanel'
import ActionForm from '../Action/ActionForm'
import {
  QuestionData,
  HandlingStatus,
  STATUS_LABELS,
  STATUS_TO_CASE_TYPE,
} from '../Action/ActionFormConstants'
import ResearchPanel from '../Research/ResearchPanel'

interface ConversationRecord extends RaRecord {
  contact_uuid?: string;
  contact_name?: string;
  case_uuid?: string;
  caseUuid?: string;
  case_type_uuid?: string;
  questions?: QuestionData[];
  subject?: string;
  notes?: string;
  research_notes?: string;
  channel: string;
  status: string;
  case_uuids?: string[];
  caseUuids?: string[];
  case_uuids_snake?: string[];
}

const PREFIX = 'xxllncConversation'
const classes = { root: `${PREFIX}-root` }

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    padding: theme.spacing(3),
    minHeight: '100vh',
    overflow: 'hidden',
  },
}))

const ConversationForm = () => {
  const record = useRecordContext<ConversationRecord>()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()

  if (!record) return null

  const [contactUuid, setContactUuid] = useState<string | null>(null)
  const [contactName, setContactName] = useState<string | null>(null)
  const [caseUuid, setCaseUuid] = useState<string | null>(null)
  const [researchNotes, setResearchNotes] = useState<string>('')
  const [currentCaseUuids, setCurrentCaseUuids] = useState<string[]>([])
  const [isLinkingActive, setIsLinkingActive] = useState<boolean>(false)

  const questionsInitialized = useRef(false)
  const [currentQuestions, setCurrentQuestions] = useState<QuestionData[]>([])
  const [researchDisabled, setResearchDisabled] = useState(false)

  const getInitialHandlingType = (): HandlingStatus => {
    const subjectMatch = Object.entries(STATUS_LABELS).find(
      ([, label]) => label === record.subject,
    )
    if (subjectMatch) return subjectMatch[0] as HandlingStatus

    const caseTypeMatch = Object.entries(STATUS_TO_CASE_TYPE).find(
      ([, uuid]) => uuid && uuid === record.case_type_uuid,
    )
    if (caseTypeMatch) return caseTypeMatch[0] as HandlingStatus

    if (record.status === 'aborted') return 'aborted'
    return 'answered'
  }

  useEffect(() => {
    if (!isLinkingActive) {
      setContactUuid(record.contact_uuid || null)
      setContactName(record.contact_name || null)

      const actueleHoofdzaak = record.case_uuid || record.caseUuid || null
      setCaseUuid(actueleHoofdzaak)

      const ruweResearch = record.research_notes || ''
      setResearchNotes(ruweResearch.split('||__CASE_JSON__:')[0])

      const ruweLijst =
        record.case_uuids || record.caseUuids || record.case_uuids_snake || []
      const databaseLijst = Array.isArray(ruweLijst) ? ruweLijst : []

      if (databaseLijst.length > 0) {
        setCurrentCaseUuids(databaseLijst)
      } else if (actueleHoofdzaak) {
        setCurrentCaseUuids([actueleHoofdzaak])
      } else {
        if (currentCaseUuids.length === 0) {
          setCurrentCaseUuids([])
        }
      }

      if (!questionsInitialized.current) {
        const initialQ =
          record.questions && record.questions.length > 0
            ? record.questions
            : [
              {
                text: record.subject || '',
                notes: record.notes || '',
                note_uuid: null,
              },
            ]
        setCurrentQuestions(initialQ)
        setResearchDisabled(getInitialHandlingType() === 'callback')
        questionsInitialized.current = true
      }
    }
  }, [record, isLinkingActive])

  const handleLinkContact = async (uuid: string, name: string) => {
    setContactUuid(uuid)
    setContactName(name)
    try {
      await dataProvider.update('conversation', {
        id: record.id,
        data: {
          contact_uuid: uuid,
          contact_name: name,
          research_notes: researchNotes,
          case_uuids: currentCaseUuids,
        },
        previousData: record,
      })
    } catch (e) {
      notify('Contact koppelen mislukt', { type: 'error' })
    }
  }

  const handleLinkCase = async (uuids: string[]) => {
    setIsLinkingActive(true)

    const uniekeUuids = Array.from(new Set(uuids)).filter(Boolean)

    setCurrentCaseUuids(uniekeUuids)
    const singleUuid = uniekeUuids.length > 0 ? uniekeUuids[0] : null
    setCaseUuid(singleUuid)

    const cleanResearch = researchNotes.split('||__CASE_JSON__:')[0]
    const updatedResearchNotes = `${cleanResearch}||__CASE_JSON__:${JSON.stringify(
      uniekeUuids,
    )}`

    try {
      await dataProvider.update('conversation', {
        id: record.id,
        data: {
          case_uuid: singleUuid,
          research_notes: updatedResearchNotes,
          case_uuids: uniekeUuids,
          case_uuids_authoritative: true,
          subject: record.subject || 'Terugbelverzoek',
        },
        previousData: record,
      })
      notify(
        singleUuid
          ? 'Zaak succesvol lokaal gekoppeld'
          : 'Zaak succesvol losgekoppeld',
        {
          type: singleUuid ? 'success' : 'info',
        },
      )
    } catch (e) {
      console.error('Zaak koppelen mislukt:', e)
      notify('Zaak koppelen mislukt', { type: 'error' })
    } finally {
      setTimeout(() => {
        setIsLinkingActive(false)
        console.log('[FRONTEND] Autosave-lock VRIJGEGEVEN.')
      }, 500)
    }
  }

  const handleQuestionsChange = async (updatedQuestions: QuestionData[]) => {
    if (isLinkingActive) return

    const cleanResearch = researchNotes.split('||__CASE_JSON__:')[0]
    const updatedResearchNotes = `${cleanResearch}||__CASE_JSON__:${JSON.stringify(
      currentCaseUuids,
    )}`

    try {
      await dataProvider.update('conversation', {
        id: record.id,
        data: {
          questions: updatedQuestions,
          subject: updatedQuestions[0]?.text || record.subject || null,
          research_notes: updatedResearchNotes,
          case_uuid: caseUuid,
          case_uuids: currentCaseUuids,
        },
        previousData: record,
      })
    } catch (e) {
      console.error('Auto-save questions failed:', e)
    }
  }

  useEffect(() => {
    if (record.status !== 'completed' && !isLinkingActive) {
      const timeoutId = setTimeout(() => {
        const schoneTekst = (record.research_notes || '').split(
          '||__CASE_JSON__:',
        )[0]
        if (researchNotes !== schoneTekst && researchNotes.trim() !== '') {
          const updatedResearchNotes = `${researchNotes}||__CASE_JSON__:${JSON.stringify(
            currentCaseUuids,
          )}`
          void dataProvider
            .update('conversation', {
              id: record.id,
              data: {
                research_notes: updatedResearchNotes,
                case_uuids: currentCaseUuids,
                case_uuid: caseUuid,
              },
              previousData: record,
            })
            .catch((err) => {
              console.error('Autosave research notes failed:', err)
            })
        }
      }, 2000)
      return () => clearTimeout(timeoutId)
    }
  }, [
    researchNotes,
    record,
    dataProvider,
    currentCaseUuids,
    caseUuid,
    isLinkingActive,
  ])

  const handleSave = async (data: {
    questions: QuestionData[];
    status: string;
    handlingType: string;
    employeeSearch?: string;
    case_type_uuid?: string;
    subject?: string;
    case_uuids?: string[];
    caseUuid?: string | null;
    case_uuids_authoritative?: boolean;
  }) => {
    try {
      const autoritatieveUuids = data.case_uuids ?? currentCaseUuids
      const autoritatieveHoofdzaak = data.caseUuid ?? caseUuid

      const cleanResearch = researchNotes.split('||__CASE_JSON__:')[0]
      const updatedResearchNotes = `${cleanResearch}||__CASE_JSON__:${JSON.stringify(
        autoritatieveUuids,
      )}`

      await dataProvider.update('conversation', {
        id: record.id,
        data: {
          questions: data.questions,
          subject: data.subject || record.subject || 'Terugbelverzoek',
          case_type_uuid: data.case_type_uuid,
          notes: data.questions
            .map((q) => q.notes)
            .filter(Boolean)
            .join('\n---\n'),
          status: 'completed',
          research_notes: updatedResearchNotes,
          contact_uuid: contactUuid,
          case_uuid: autoritatieveHoofdzaak,
          case_uuids: autoritatieveUuids,
          case_uuids_authoritative: true,
        },
        previousData: record,
      })

      for (const q of data.questions) {
        if (q.text.trim() && (contactUuid || autoritatieveHoofdzaak)) {
          try {
            await dataProvider.create('note', {
              data: {
                contact_uuid: contactUuid || undefined,
                case_uuid: autoritatieveHoofdzaak || undefined,
                subject: q.text,
                content: q.notes || q.text,
                channel: record.channel,
              },
            })
          } catch (e) {
            console.error('Note creation failed:', e)
          }
        }
      }

      notify(
        `Antwoord bij telefoongesprek met ${contactName} is succesvol verwerkt en verzonden!`,
        { type: 'success' },
      )

      if (refresh) refresh()
    } catch (e) {
      console.error('Opslaan mislukt:', e)
      notify('Opslaan mislukt', { type: 'error' })
    }
  }

  return (
    <StyledBox className={classes.root}>
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={3} sx={{ pt: '0 !important' }}>
          <ContactPanel
            contactUuid={contactUuid}
            contactName={contactName}
            onLinkContact={(uuid, name) => {
              void handleLinkContact(uuid, name)
            }}
          />
        </Grid>

        <Grid item xs={12} md={3.5} sx={{ pt: '0 !important' }}>
          <ActionForm
            channel={record.channel}
            questions={currentQuestions}
            status={record.status}
            caseUuid={caseUuid}
            caseUuids={currentCaseUuids}
            handlingType={getInitialHandlingType()}
            onLinkCase={(uuids: string[]) => {
              void handleLinkCase(uuids)
            }}
            onSave={(data) => {
              void handleSave(data)
            }}
            onQuestionsChange={(updated: QuestionData[]) => {
              void handleQuestionsChange(updated)
            }}
            onHandlingTypeChange={(type: HandlingStatus) => {
              setResearchDisabled(type === 'callback')
            }}
          />
        </Grid>

        <Grid item xs={12} md={5.5} sx={{ pt: '0 !important' }}>
          <ResearchPanel
            disabled={record.status === 'completed' || researchDisabled}
            value={researchNotes}
            onChange={setResearchNotes}
            contactUuid={contactUuid}
            caseUuid={caseUuid}
            onLinkCase={(uuid: string) => {
              void handleLinkCase([...currentCaseUuids, uuid])
            }}
          />
        </Grid>
      </Grid>
    </StyledBox>
  )
}

const ConversationEdit = () => (
  <Edit component="div" actions={false} sx={{ '& .RaEdit-main': { mt: 0 } }}>
    <ConversationForm />
  </Edit>
)

export default ConversationEdit